import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Book, ArrowLeft, Clock, Wifi, AlertCircle, Home, CheckCircle, Phone, Mail, MapPin, Car, Map, Images, FileText, Loader2 } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { trackEvent } from '../../utils/analytics';
import { useHubProperty, hubPath } from './hubConfig';
import { HubEmailGate, hasHubAccess } from './HubEmailGate';
import { HubLogo } from './HubBrand';
import { MediaGrid } from './MediaGrid';
import { sendGuidebookEvent } from './guidebookEvents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Section = ({ id, icon: Icon, title, children }) => (
  <AccordionItem value={id} data-testid={`guidebook-section-${id}`} className="bg-slate-800/50 border border-slate-700 rounded-xl px-4">
    <AccordionTrigger data-testid={`guidebook-toggle-${id}`} className="text-white hover:no-underline py-4 text-left">
      <span className="flex items-center gap-3"><Icon className="h-5 w-5 text-amber-400 flex-shrink-0" />{title}</span>
    </AccordionTrigger>
    <AccordionContent className="text-slate-300 text-sm pb-4">{children}</AccordionContent>
  </AccordionItem>
);

const Paragraphs = ({ text }) => text ? text.split(/\n+/).map((p, i) => <p key={i} className="mb-2 whitespace-pre-line">{p}</p>) : null;

const renderSection = (key, gb, wifi, slug) => {
  switch (key) {
    case 'checkin':
      return (
        <Section key={key} id={key} icon={Clock} title="Check-in & Check-out">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="bg-slate-700/50 rounded-lg p-3"><p className="text-xs text-slate-400">Check-in</p><p className="text-white font-semibold">{gb.check_in}</p></div>
            <div className="bg-slate-700/50 rounded-lg p-3"><p className="text-xs text-slate-400">Check-out</p><p className="text-white font-semibold">{gb.check_out}</p></div>
          </div>
          {gb.address && <p className="flex gap-2 mb-2"><MapPin className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />{gb.address}</p>}
          {gb.special_notes && <p className="mb-2">{gb.special_notes}</p>}
          {gb.parking_note && <p className="flex gap-2"><Car className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />{gb.parking_note}</p>}
        </Section>
      );
    case 'wifi':
      return (
        <Section key={key} id={key} icon={Wifi} title="Wi-Fi">
          <p>Network: <span data-testid="guidebook-wifi-network" className="font-mono text-white">{wifi?.network_name || 'Contact host'}</span></p>
          <p>Password: <span data-testid="guidebook-wifi-password" className="font-mono text-white">{wifi?.password || 'Contact host'}</span></p>
          <Link to={hubPath(slug, 'wifi')} className="text-amber-400 underline text-xs mt-2 inline-block">Open one-tap Wi-Fi page</Link>
        </Section>
      );
    case 'rules':
      return gb.house_rules.length > 0 && (
        <Section key={key} id={key} icon={AlertCircle} title="House Rules">
          <ul className="space-y-2">{gb.house_rules.map((r, i) => <li key={i}><span className="text-white font-medium">{r.rule}</span>{r.detail && <> — {r.detail}</>}</li>)}</ul>
        </Section>
      );
    case 'essentials':
      return gb.essentials.length > 0 && (
        <Section key={key} id={key} icon={Home} title="Amenities & How-Tos">
          <ul className="space-y-2">{gb.essentials.map((e, i) => <li key={i}><span className="text-white font-medium">{e.title}:</span> {e.description}</li>)}</ul>
        </Section>
      );
    case 'house_map':
      return (gb.house_map.notes || gb.house_map.media.length > 0) && (
        <Section key={key} id={key} icon={Map} title="House Map / Where Things Are">
          <Paragraphs text={gb.house_map.notes} />
          <MediaGrid items={gb.house_map.media} testId="guidebook-house-map" />
        </Section>
      );
    case 'gallery':
      return gb.gallery.length > 0 && (
        <Section key={key} id={key} icon={Images} title="Photos & Videos">
          <MediaGrid items={gb.gallery} testId="guidebook-gallery" />
        </Section>
      );
    case 'checkout':
      return gb.checkout_steps.length > 0 && (
        <Section key={key} id={key} icon={CheckCircle} title="Checkout Checklist">
          <ol className="list-decimal pl-5 space-y-1">{gb.checkout_steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
        </Section>
      );
    case 'contact':
      return gb.contacts.length > 0 && (
        <Section key={key} id={key} icon={Phone} title="Contact Your Host">
          <ul className="space-y-2">
            {gb.contacts.map((c, i) => (
              <li key={i} className="flex items-center gap-2">
                {c.type === 'email' ? <Mail className="h-4 w-4 text-amber-400" /> : <Phone className="h-4 w-4 text-amber-400" />}
                <a href={c.type === 'email' ? `mailto:${c.value}` : `tel:${c.value.replace(/[^\d+]/g, '')}`} className="text-white underline">{c.value}</a>
                {c.label && <span className="text-slate-500 text-xs">{c.label}</span>}
              </li>
            ))}
          </ul>
        </Section>
      );
    default: {
      const custom = gb.custom_sections.find((c) => `custom-${c.id}` === key);
      return custom && (
        <Section key={key} id={key} icon={FileText} title={custom.title}>
          <Paragraphs text={custom.body} />
          <MediaGrid items={custom.media} testId={`guidebook-custom-${custom.id}`} />
        </Section>
      );
    }
  }
};

const GuidebookSections = ({ gb, wifi, slug, propertyId }) => {
  const visible = gb.section_order.filter((k) => !gb.hidden_sections.includes(k));
  useEffect(() => { sendGuidebookEvent(propertyId, 'open'); }, [propertyId]);
  return (
    <div className="space-y-4">
      {gb.intro && <p data-testid="guidebook-intro" className="text-slate-300 text-sm whitespace-pre-line">{gb.intro}</p>}
      <Accordion type="single" collapsible defaultValue={visible[0]} className="space-y-3"
        onValueChange={(v) => v && sendGuidebookEvent(propertyId, 'section', v)}>
        {visible.map((key) => renderSection(key, gb, wifi, slug))}
      </Accordion>
    </div>
  );
};

const HubGuidebook = () => {
  const { slug, propertyId, hubProperty } = useHubProperty();
  const [unlocked, setUnlocked] = useState(hasHubAccess('guidebook', propertyId));
  const [wifi, setWifi] = useState(null);
  const [gb, setGb] = useState(null);

  useEffect(() => {
    trackEvent('guidebook_view', { property_id: propertyId, source: 'guest_hub' });
    fetch(`${API_URL}/api/public/wifi/${propertyId}`).then((r) => (r.ok ? r.json() : null)).then(setWifi);
    fetch(`${API_URL}/api/public/guidebook/${propertyId}`).then((r) => (r.ok ? r.json() : null)).then(setGb);
  }, [propertyId]);

  return (
    <div data-testid="hub-guidebook-page" className="min-h-screen bg-slate-900 max-w-lg mx-auto">
      <div className="px-4 pt-6 pb-4">
        <Link to={hubPath(slug)} data-testid="hub-back-link" className="inline-flex items-center text-slate-400 hover:text-white mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Hub
        </Link>
        <div className="text-center">
          <div className="mb-6"><HubLogo /></div>
          <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4"><Book className="h-8 w-8 text-white" /></div>
          <h1 className="text-2xl font-bold text-white mb-1">House Guidebook</h1>
          <p className="text-slate-400 text-sm">{hubProperty?.name}</p>
        </div>
      </div>
      <div className="px-4 pb-8">
        {unlocked ? (
          gb ? <GuidebookSections gb={gb} wifi={wifi} slug={slug} propertyId={propertyId} /> : <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-amber-400" /></div>
        ) : (
          <HubEmailGate source="guidebook" propertyId={propertyId} slug={slug}
            prompt="Enter your email to unlock check-in details, house rules and everything you need for your stay."
            buttonLabel="Open Guidebook" onUnlocked={() => setUnlocked(true)} />
        )}
      </div>
      <div className="px-4 pb-8 text-center">
        <Link to={hubPath(slug, 'local-guide')} data-testid="guidebook-to-local-guide" className="text-amber-400 text-sm underline">
          Looking for restaurants & things to do? See the Local Guide
        </Link>
      </div>
    </div>
  );
};

export default HubGuidebook;
